import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import * as api from '@/features/backlog/api';
import { Board, Item } from '@/features/backlog/types';
import { taskTypeMeta } from '@/features/backlog/constants';
import { taskPath } from '@/constants/routes';
import { TaskTypeGlyph, X } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';

const FLYOUT_W = 340;
const MAX_DEPTH = 16; // safety cap on the parent walk

/** One node in the ancestor tree — a type glyph + key + title + board, opening the item in a new tab. */
function Node({ item, boardById, muted, current }: { item: Item; boardById: Map<string, Board>; muted?: boolean; current?: boolean }) {
  const b = boardById.get(item.board_id);
  const open = () => { if (b) window.open(taskPath(b.slug, item.key), '_blank', 'noopener,noreferrer'); };
  return (
    <button
      type="button"
      onClick={open}
      disabled={!b}
      title={item.title}
      className={cn(
        'flex w-full min-w-0 items-center gap-1.5 rounded-lg px-1.5 py-1 text-left transition-colors',
        b && 'hover:bg-layer',
        current && 'bg-accent/10 ring-1 ring-inset ring-accent/30',
      )}
    >
      <TaskTypeGlyph type={item.type} color={taskTypeMeta(item.type).color} />
      <span className="item-key shrink-0 rounded px-1 py-px tnum text-[9.5px] font-semibold">{item.key}</span>
      <span className={cn('min-w-0 flex-1 truncate text-[12px]', muted ? 'text-fg-faint' : current ? 'font-medium text-fg' : 'text-fg-soft')}>{item.title || 'Untitled'}</span>
      {b ? <span className="shrink-0 truncate rounded-full bg-layer px-1.5 text-[9px] font-medium text-fg-faint">{b.name}</span> : null}
    </button>
  );
}

/**
 * A flyout that walks an item's ancestor tree — its Epic link (if any) and its parent chain from the
 * root down to the item — resolved on open via the board-gated /items/by-ids batch. Positioned near the
 * clicked tree icon; closes on outside click / Escape.
 */
export default function AncestorFlyout({ item, rect, boardById, onClose }: { item: Item; rect: DOMRect; boardById: Map<string, Board>; onClose: () => void }) {
  // `chain` is root-first and INCLUDES the item itself at the end; `epic` is the item's epic link.
  const [chain, setChain] = useState<Item[]>([item]);
  const [epic, setEpic] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: rect.left, top: rect.bottom + 6 });
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const parents: Item[] = [];
      const seen = new Set<string>([item.id]);
      let cur = item;
      for (let i = 0; i < MAX_DEPTH && cur.parent_id && !seen.has(cur.parent_id); i++) {
        const [p] = await api.itemsByIds([cur.parent_id]);
        if (!p) break;
        parents.unshift(p); // build root-first
        seen.add(p.id);
        cur = p;
      }
      const ep = item.epic_id ? (await api.itemsByIds([item.epic_id]))[0] ?? null : null;
      if (!cancelled) { setChain([...parents, item]); setEpic(ep); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [item]);

  // Outside click + Escape close.
  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (!panelRef.current?.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  // Position below the icon, flipping ABOVE when the panel would overflow the bottom (and left when it
  // would overflow the right). Re-measured after the tree loads (its height grows), and clamped on-screen.
  useLayoutEffect(() => {
    const h = panelRef.current?.offsetHeight ?? 0;
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - FLYOUT_W - 12));
    let top = rect.bottom + 6;
    if (top + h > window.innerHeight - 12) {
      const above = rect.top - h - 6;
      top = above >= 12 ? above : Math.max(12, window.innerHeight - h - 12);
    }
    setPos({ left, top });
  }, [rect, chain, epic, loading]);

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[1000] max-h-[70vh] w-[340px] overflow-auto rounded-xl border border-line bg-surface-raised p-2 shadow-2xl shadow-black/40"
      style={{ left: pos.left, top: pos.top }}
    >
      <div className="mb-1 flex items-center gap-2 px-1.5 pb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">Ancestor tree</span>
        <button type="button" onClick={onClose} aria-label="Close" className="ml-auto flex h-6 w-6 items-center justify-center rounded-lg text-fg-faint hover:bg-layer hover:text-fg"><X className="h-3.5 w-3.5" /></button>
      </div>

      {epic ? (
        <div className="mb-1.5">
          <div className="px-1.5 pb-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-fg-ghost">Epic</div>
          <Node item={epic} boardById={boardById} muted />
        </div>
      ) : null}

      <div className="px-1.5 pb-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-fg-ghost">
        {chain.length > 1 ? 'Parent chain' : 'This item'}
      </div>
      <div className="relative">
        {chain.map((node, i) => (
          <div key={node.id} className="relative" style={{ paddingLeft: i * 14 }}>
            {i > 0 ? (
              <span className="pointer-events-none absolute text-fg-ghost" style={{ left: (i - 1) * 14 + 5, top: 2, fontSize: 12, lineHeight: '16px' }}>└</span>
            ) : null}
            <Node item={node} boardById={boardById} current={i === chain.length - 1} />
          </div>
        ))}
      </div>
      {loading ? <div className="px-1.5 pt-1 text-[11px] text-fg-ghost">Loading tree…</div> : null}
    </div>,
    document.body,
  );
}
